import {isNull, omit} from 'lodash';
import React, {FC, Key, memo, ReactNode, useCallback, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';

import {isElement} from '../../../../../../libs/shared/helpers/src/browser/domHelpers';
import {usePrevious, useTimeout} from '../../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {Handler, HandlerOf, RendererOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {isShallowEqual} from '../../../../../../libs/shared/helpers/src/utilities/objectHelpers';
import {InteractionParent} from '../../../../../../libs/shared/ui/src/interactionTracking/interactionParent';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {usePush} from '../../../../../../srcCommon/push/pushContext';
import {
  COMMENT_JUMP_ANIMATION_DURATION_MS,
  PINNED_COMMENT_ANIMATION_DURATION_MS,
} from '../../../../../helpers/animationHelpers';
import {
  findSingleImageAttachment,
  hasUpdatedReactions,
  isCommentOnlyAnEmoji,
  renderCommentTitle,
} from '../../../../../helpers/commentViewerHelpers';
import {ActivityModel} from '../../../../../models/api/activityModel';
import {
  AnnotationHighlightCommentModel,
  AnnotationHighlightModel,
} from '../../../../../models/api/annotationModel';
import {AttachmentModel} from '../../../../../models/api/attachmentModel';
import {CommentModel, CommentStatusesEnum} from '../../../../../models/api/commentModel';
import {ConversationActionContextsEnum} from '../../../../../models/api/conversationCommonTypes';
import {ReminderModel} from '../../../../../models/api/reminderModel';
import {TeammateModel} from '../../../../../models/api/teammateModel';
import {VisualRecipientModel} from '../../../../../models/api/visualRecipientModel';
import {EmojiMap} from '../../../../../services/emojis/emojis';
import {CellsContext} from '../../../../../store/panels/cells/context/cellsContext';
import {NewPanelId, ViewerPanelId} from '../../../../../store/panels/panelStateBase';
import {TimelineVariationsEnum} from '../../../../../store/timelineVariations/timelineVariationSelectors';
import {AnnotationsHighlightViewerContainer} from '../../../../common/annotation/annotationsHighlightViewerContainer';
import {TeammateAvatar} from '../../../../common/avatars/teammateAvatar';
import {useVariableHeightElement} from '../../../../common/scroll/useVariableHeightElement';
import {PrivateCommentInfoBubble} from '../../../../infoBubbles/privateCommentInfoBubble';
import {EditCommentComposerContainer} from '../../commentComposer/editCommentComposerContainer';
import {BasicViewerResnoozeContainer} from '../basic/basicViewerResnoozeContainer';
import {CommentBase} from './commentBase';
import {CommentReactionsProps} from './commentBaseReactions';
import {CommentBody, StyledEmojiDiv} from './commentBody';

/*
 * Constants.
 */

export const recentActivityThreshold = 10 * 60 * 1000; // 10 minutes.

/*
 * Props.
 */

export interface CommentViewerProps extends CommentReactionsProps {
  panelId: ViewerPanelId;
  comment: CommentModel;
  author: TeammateModel;
  cellsContext: CellsContext;
  allAliases: ReadonlySet<string>;
  mentionableAliases: ReadonlySet<string>;
  userAlias?: string;
  hasSameAuthorAsPrevious: boolean;
  isReferencingTheSameMessageAsPrevious: boolean;
  isLastSendingComment?: boolean;
  isFromUser?: boolean;
  isNewUnread?: boolean;
  isUnread?: boolean;
  annotations: ReadonlyArray<AnnotationHighlightCommentModel>;
  attachments: ReadonlyArray<AttachmentModel>;
  onReferencedActivityClick?: HandlerOf<number>;
  editComment?: Handler;
  deleteComment?: Handler;
  retryComment?: Handler;
  previousActivity?: ActivityModel;
  isVisible?: boolean;
  isLastActivity?: boolean;
  canAddReactions?: boolean;
  onReplyClick?: Handler;
  referencedAuthor?: TeammateModel;
  referencedComment?: CommentModel;
  referencedRecipient?: VisualRecipientModel;
  userId?: number;
  shouldShowDemoInboxTip?: boolean;
  isVisibleOnlyToUser?: boolean;
  isPinned?: boolean;
  pinnedByTeammate?: TeammateModel;
  togglePinComment?: Handler;
  jumpKey?: Key;
  isHighlighted?: boolean;
  isEditing?: boolean;
  emojiValidationMap: EmojiMap;
  namespace: string;
  hasQuoteComment: boolean;
  isFirstComment?: boolean;
  onQuoteInCommentClick?: HandlerOf<AnnotationHighlightModel | AnnotationHighlightCommentModel>;
  jumpSourceActivityId?: number;
  conversationId: number;
  canceledReminder?: ReminderModel;
  conversationActionContext: ConversationActionContextsEnum | undefined;
  timelineVariation: TimelineVariationsEnum;
}

/*
 * Style.
 */

const StyledTimelineEmojiDiv = styled(StyledEmojiDiv)<{$hasSameAuthorAsPrevious: boolean}>`
  img {
    margin-top: ${(p) => (p.$hasSameAuthorAsPrevious ? '2px' : '-1px')};
  }
`;

/*
 * Component.
 */

export const CommentViewer: FC<CommentViewerProps> = memo((props: CommentViewerProps) => {
  const {
    comment,
    author,
    isUnread,
    previousActivity,
    hasSameAuthorAsPrevious,
    referencedAuthor,
    referencedComment,
    referencedRecipient,
    userId,
    allAliases,
    mentionableAliases,
    userAlias,
    attachments,
    onReferencedActivityClick,
    isEditing,
    panelId,
    emojiValidationMap,
    deleteComment,
    isReferencingTheSameMessageAsPrevious,
    hasQuoteComment,
    onQuoteInCommentClick,
    annotations,
    jumpSourceActivityId,
    jumpKey,
    timelineVariation,
  } = props;

  const {isConnected} = usePush();
  const viewerRef = useRef<HTMLDivElement>(null);

  // Special cases.
  const isSingleImageAttachment = Boolean(findSingleImageAttachment(comment));
  const isReply = Boolean(referencedAuthor);
  const isAnnotation = Boolean(comment.annotation);
  const isAllEmoji = isCommentOnlyAnEmoji(comment, isReply, attachments, emojiValidationMap);
  const hasError = comment.status === CommentStatusesEnum.FAILED;

  // Hide the timestamp if the delta between this comment and the (possible) previous comment
  // is by the same author and was less than 10min ago.
  const previousActivityDelta = previousActivity && comment.sentAt - previousActivity.date;
  const isPreviousActivityRecent = previousActivityDelta && previousActivityDelta < recentActivityThreshold;
  const shouldHideTimestamp = Boolean(hasSameAuthorAsPrevious && isPreviousActivityRecent);

  // Hide the title if the comment doesn't reference somebody and the author is the same.
  const shouldHideTitle = hasSameAuthorAsPrevious && !isReply;

  const useRenderAvatar = useCallback(
    () => <TeammateAvatar teammate={author} size={VisualSizesEnum.LARGE} />,
    [author],
  );
  const useRenderTitle = useCallback(
    () => renderCommentTitle(author, referencedAuthor, userId, isEditing),
    [author, referencedAuthor, userId, isEditing],
  );

  const renderOneBigEmoji: RendererOf<ReactNode> = useCallback(
    (emojiNode) => (
      <StyledTimelineEmojiDiv $hasSameAuthorAsPrevious={hasSameAuthorAsPrevious}>
        {emojiNode}
      </StyledTimelineEmojiDiv>
    ),
    [hasSameAuthorAsPrevious],
  );

  // We want to animate the comment background color when it gets pinned for a few seconds,
  // so we set `wasJustPinned` to true during that time.
  const wasJustPinnedAnimationDurationMs = PINNED_COMMENT_ANIMATION_DURATION_MS;
  const previousIsPinned = usePrevious(props.isPinned);
  const wasJustPinned = useFlagWithTimeout(
    !isNull(previousIsPinned) && Boolean(!previousIsPinned && props.isPinned),
    wasJustPinnedAnimationDurationMs,
  );

  // We want to flash the comment background color when it gets jumped to (e.g. from a referenced or pinned comment)
  const wasJustHighlightedAnimationDurationMs = COMMENT_JUMP_ANIMATION_DURATION_MS;
  const shouldSetWasJustHighlighted = useShouldSetWasJustHighlighted(props);
  const wasJustHighlighted = useFlagWithTimeout(
    shouldSetWasJustHighlighted,
    wasJustHighlightedAnimationDurationMs,
  );

  const previousJumpKey = usePrevious(jumpKey);
  const previousJumpSourceActivityId = usePrevious(jumpSourceActivityId);

  // Animation where the highlighted section of an annotated comment turns blue when the user jumps to it.
  useEffect(() => {
    const jumpKeyChanged = previousJumpKey !== jumpKey;
    const jumpSourceActivityIdChanged = previousJumpSourceActivityId !== jumpSourceActivityId;
    const shouldJump = jumpSourceActivityId !== undefined && (jumpKeyChanged || jumpSourceActivityIdChanged);

    if (!hasQuoteComment || !shouldJump) {
      return;
    }

    const viewerElement = viewerRef.current;

    if (!viewerElement) {
      return;
    }

    viewerElement.querySelector(`[data-source-activity-id="${jumpSourceActivityId}"]`)?.scrollIntoView();

    viewerElement.querySelectorAll(`[data-source-activity-id="${jumpSourceActivityId}"]`).forEach((el) => {
      el.classList.add('front_annotation_focused');
      el.addEventListener('animationend', endQuoteCommentJumpAnimation);
    });
  }, [hasQuoteComment, jumpKey, jumpSourceActivityId]);

  // We inject the AnnotationsHighlightViewerContainer as a prop to comment body, because the
  // AnnotationsHighlightViewerContainer itself instantiates CommentBody via the FloatingCommentDropdown.
  // Importing AnnotationsHighlightViewerContainer in the CommentViewer allows us to avoid the circular
  // dependency issue.
  const textWrapper = (child: ReactNode) =>
    maybeRenderAnnotationsHighlightViewerWrapper(
      child,
      hasQuoteComment,
      panelId,
      comment,
      annotations,
      onQuoteInCommentClick,
    );

  // Report changes in our height to the VariableHeightScroller when these values change.
  useVariableHeightElement(viewerRef, [props.reactions, props.isPinned, props.comment.unfurls, isEditing]);

  return (
    <InteractionParent interactionId={`comment-${comment.uid}`}>
      <CommentBase
        date={comment.sentAt}
        lastEditedAt={comment.lastEditedAt}
        renderAvatar={useRenderAvatar}
        renderTitle={useRenderTitle}
        shouldHideTitle={shouldHideTitle}
        shouldHideReplyArrow={isEditing}
        shouldHideTimestamp={shouldHideTimestamp}
        shouldHideOperations={isEditing}
        shouldHideReactions={isEditing}
        isFaded={isSendingStatus(comment.status) && !isConnected}
        isFromUser={props.isFromUser}
        isLastSendingOffline={props.isLastSendingComment && !isConnected}
        isNewUnread={props.isNewUnread}
        isUnread={isUnread}
        hasError={hasError}
        editComment={props.editComment}
        deleteComment={props.deleteComment}
        retryComment={props.retryComment}
        shouldHideBackground={!isReply && !isAnnotation && (isSingleImageAttachment || isAllEmoji)}
        reactions={props.reactions}
        canAddReactions={props.canAddReactions}
        onToggleReaction={props.onToggleReaction}
        onAddReaction={props.onAddReaction}
        userId={userId}
        isVisible={props.isVisible}
        isVisibleOnlyToUser={props.isVisibleOnlyToUser}
        onReplyClick={props.onReplyClick}
        isReply={isReply}
        viewerRef={viewerRef}
        shouldShowDemoInboxTip={props.shouldShowDemoInboxTip}
        isPinned={props.isPinned}
        pinnedByTeammate={props.pinnedByTeammate}
        togglePinComment={props.togglePinComment}
        hasSameAuthorAsPrevious={hasSameAuthorAsPrevious}
        interactionId={comment.uid}
        shouldRenderWithoutBubble={isEditing}
        isFirstComment={props.isFirstComment}
        wasJustPinned={wasJustPinned}
        wasJustHighlighted={wasJustHighlighted}
        timelineVariation={timelineVariation}
      >
        {isEditing && deleteComment ? (
          <EditCommentComposerContainer
            panelId={panelId}
            conversationId={comment.conversationId}
            comment={comment}
            onDeleteClick={deleteComment}
          />
        ) : (
          <CommentBody
            comment={comment}
            referencedAuthor={referencedAuthor}
            referencedComment={referencedComment}
            referencedRecipient={referencedRecipient}
            allAliases={allAliases}
            mentionableAliases={mentionableAliases}
            userAlias={userAlias}
            attachments={attachments}
            shouldAlwaysUseMultiAttachmentStyle
            onReferencedActivityClick={onReferencedActivityClick}
            renderOneBigEmoji={renderOneBigEmoji}
            panelId={props.panelId}
            namespace={props.namespace}
            textWrapper={textWrapper}
            hasQuoteComment={hasQuoteComment}
            isReferencingTheSameMessageAsPrevious={
              hasSameAuthorAsPrevious && isReferencingTheSameMessageAsPrevious
            }
          />
        )}
      </CommentBase>
      {props.isFromUser && props.isVisibleOnlyToUser && <PrivateCommentInfoBubble targetRef={viewerRef} />}
      {maybeRenderResnoozeNotification(props)}
    </InteractionParent>
  );
}, customEqualityCheck);

function maybeRenderResnoozeNotification(props: CommentViewerProps) {
  const {canceledReminder, cellsContext, conversationId, conversationActionContext} = props;
  if (!conversationActionContext || !canceledReminder) {
    return null;
  }

  return (
    <BasicViewerResnoozeContainer
      canceledReminder={canceledReminder}
      cellsContext={cellsContext}
      conversationId={conversationId}
      conversationActionContext={conversationActionContext}
    />
  );
}

export function maybeRenderAnnotationsHighlightViewerWrapper(
  child: ReactNode,
  hasQuoteComment: boolean,
  panelId: NewPanelId,
  comment: CommentModel,
  annotations: ReadonlyArray<AnnotationHighlightCommentModel>,
  onQuoteInCommentClick?: HandlerOf<AnnotationHighlightModel | AnnotationHighlightCommentModel>,
) {
  if (!hasQuoteComment) {
    return child;
  }

  // This FF has an awkward name, but the inline annotation/highlight feature (message-annotations) is a prerequisite
  // for the quote-comment feature.
  const hasMessageAnnotations = true;

  return (
    <AnnotationsHighlightViewerContainer
      annotations={annotations}
      onQuoteInCommentClick={onQuoteInCommentClick}
      panelId={panelId}
      conversationId={comment.conversationId}
      hasMessageAnnotations={hasMessageAnnotations}
      isAnnotatingComment
    >
      {child}
    </AnnotationsHighlightViewerContainer>
  );
}

// If this function returns true, we'll skip a rerender.
function customEqualityCheck(prev: CommentViewerProps, next: CommentViewerProps) {
  // Rerender if the reactions have changed.
  if (hasUpdatedReactions(prev.reactions, next.reactions)) {
    return false;
  }

  // We ignore isVisible for search highlighting (PB-16627).
  if (
    !isShallowEqual(omit(prev, 'isVisible'), omit(next, 'isVisible')) ||
    (prev.isNewUnread && prev.isVisible !== next.isVisible)
  ) {
    return false;
  }

  return true;
}

/*
 * Helpers.
 */

function isSendingStatus(status: CommentStatusesEnum) {
  return status === CommentStatusesEnum.PENDING || status === CommentStatusesEnum.SENDING;
}

function useFlagWithTimeout(shouldSetFlag: boolean, timeoutMs: number) {
  const [safeSetTimeout] = useTimeout();
  const [flag, setFlag] = useState(false);

  useEffect(() => {
    if (shouldSetFlag) {
      setFlag(true);
      safeSetTimeout(() => {
        setFlag(false);
      }, timeoutMs);
    }
  }, [safeSetTimeout, setFlag, shouldSetFlag, timeoutMs]);

  return flag;
}

function useShouldSetWasJustHighlighted(props: CommentViewerProps) {
  const {jumpKey, isHighlighted, hasQuoteComment, jumpSourceActivityId, annotations} = props;
  const previousJumpKey = usePrevious(jumpKey);
  const previousIsHighlighted = usePrevious(isHighlighted);

  const isQuoteCommentJump =
    hasQuoteComment &&
    jumpSourceActivityId !== undefined &&
    annotations.length > 0 &&
    annotations.some((annotation) => annotation.commentActivityId === jumpSourceActivityId);

  // Trigger a highlight animation when the flag transitions from falsy to truthy.
  if (!previousIsHighlighted && isHighlighted && !isQuoteCommentJump) {
    return true;
  }

  // Re-trigger a highlight animation when the flag remains truthy, and the jump key changes.
  if (isHighlighted && jumpKey !== previousJumpKey && !isQuoteCommentJump) {
    return true;
  }

  return false;
}

function endQuoteCommentJumpAnimation(e: Event) {
  const {target} = e;
  if (!target) {
    return;
  }

  target.removeEventListener('animationend', endQuoteCommentJumpAnimation);
  if (!isElement(target)) {
    return;
  }

  target.classList.remove('front_annotation_focused');
}
